// NOTE: Mirror of the server's connector-approval contract
// (apps/nsai/server/src/connector-approval/connector-approval.types.ts).
// The server intentionally does not depend on @frontend-shared, so these
// enums are kept in sync by hand — the same pattern used for EIntegrationProvider.

// NOTE: Per-connector default approval level (ChatGPT-style tiered preset).
export enum EConnectorApprovalLevel {
  ALWAYS_ASK = "always_ask",
  ASK_ON_CHANGES = "ask_on_changes",
  ASK_ON_IMPORTANT = "ask_on_important",
  NEVER_ASK = "never_ask",
}

// NOTE: Per-tool override / resolved stance (Claude-style Allow / Ask / Block).
export enum EToolApprovalStance {
  ALLOW = "allow",
  ASK = "ask",
  BLOCK = "block",
}

// NOTE: Action sensitivity used to render a tool's default behaviour under a
// given connector level in the settings UI.
export enum EToolSensitivity {
  READ = "read",
  WRITE = "write",
  DESTRUCTIVE = "destructive",
}

// NOTE: NSAI-5497/5498 — the connector rail a catalog action belongs to. A
// provider's advertised actions are the UNION of its native and Pipedream rails,
// but the runtime provisions only the rail(s) the user actually connected, so the
// settings UI filters actions to the connected rail(s) by this field. String
// values match the server wire format (EConnectorActionRail in
// connector-approval.types.ts).
export enum EConnectorActionRail {
  NATIVE = "native",
  PIPEDREAM = "pipedream",
}

// NOTE: NSAI-5497/5498 — stable discriminant for the per-action rail badge, so web and
// mobile render the same three states (the badge is only shown when both rails are
// connected — see shouldShowActionRailBadge).
export enum EActionRailBadge {
  NATIVE = "native",
  PIPEDREAM = "pipedream",
  BOTH = "both",
}

export type TToolApprovalOverrides = Record<string, EToolApprovalStance>;

export interface IConnectorApprovalSetting {
  provider: string;
  level: EConnectorApprovalLevel;
  toolOverrides: TToolApprovalOverrides;
}

export interface IConnectorApprovalSettingsResponse {
  defaultLevel: EConnectorApprovalLevel;
  connectors: IConnectorApprovalSetting[];
}

export interface IUpsertConnectorApprovalSettingPayload {
  provider: string;
  level?: EConnectorApprovalLevel;
  toolOverrides?: TToolApprovalOverrides;
}

// NOTE: Static per-connector action metadata served to the settings UI.
export interface IConnectorActionInfo {
  name: string;
  title?: string;
  description: string;
  sensitivity: EToolSensitivity;
  // NOTE: NSAI-5497/5498 — the connector rail(s) this action runs on. A native
  // action with a Pipedream twin carries BOTH; pipedream-only or native-only actions
  // carry one. Drives the rail badge and the connected-rail filter in the settings UI.
  rails: EConnectorActionRail[];
}

// NOTE: NSAI-5354 — one connected MCP server as the approval UI consumes it. Mirrors
// IConnectorCatalogEntry (label + actions) so the same action rows can render it, plus the
// two fields only MCP needs: `scopeKey`, the stable `mcp-server:<hash>` the policy is stored
// under, and `isUnavailable`, set when the tool list could not be discovered (server down,
// credentials expired) — the level can still be configured in that state.
export interface IMcpApprovalTarget {
  scopeKey: string;
  label: string;
  url: string;
  actions: IConnectorActionInfo[];
  isUnavailable: boolean;
}

export interface IConnectorCatalogEntry {
  provider: string;
  label: string;
  actions: IConnectorActionInfo[];
}

// NOTE: Organization-level (owner-managed) policy for a connector. The org layer
// can only make a member's effective policy stricter.
export interface IOrgConnectorApprovalPolicy {
  provider: string;
  minLevel: EConnectorApprovalLevel;
  lockedToolStances: TToolApprovalOverrides;
}

export interface IOrgConnectorApprovalPolicyResponse {
  policies: IOrgConnectorApprovalPolicy[];
}

export interface IUpsertOrgConnectorApprovalPolicyPayload {
  provider: string;
  minLevel?: EConnectorApprovalLevel;
  lockedToolStances?: TToolApprovalOverrides;
}

// NOTE: "follow" represents an action with no explicit override — it inherits
// the connector-level default. The other values map to EToolApprovalStance.
// Shared by the web and mobile per-action stance controls.
export type TActionStanceValue = "follow" | EToolApprovalStance;

// NOTE: Payload emitted when a single action's stance changes in the settings
// UI (segmented control on web, pill row on mobile).
export interface IConnectorActionStanceChangeProps {
  toolName: string;
  stance: TActionStanceValue;
}

// NOTE: Input to the UI mirror of the server's stance resolver (level ×
// sensitivity). The server stays the source of truth at runtime — this only
// drives the "follows level" hint.
export interface IResolveEffectiveStanceProps {
  level: EConnectorApprovalLevel;
  sensitivity: EToolSensitivity;
}

// NOTE: Input to the shared org-minimum label resolver. `translate` is the
// caller's i18next `t` bound to the connectorApproval namespace — typed as a
// plain lookup so this stays free of per-app i18next resource generics.
export interface IResolveConnectorApprovalLevelLabelProps {
  translate: (key: string) => string;
  level: EConnectorApprovalLevel;
}

// NOTE: NSAI-5497/5498 — input to filterCatalogActionsByConnectedRails. `connectedRails`
// is the set of rails the user has actually connected for the provider; an action is kept
// when it runs on at least one of them.
export interface IFilterCatalogActionsByConnectedRailsProps {
  actions: IConnectorActionInfo[];
  connectedRails: EConnectorActionRail[];
}
